import { sortBy } from 'lodash';
import { Adapter } from '../interfaces';
import { DetectionEngineOutputData } from './models';
import { namespace, WorkerStoreSlice } from '@worker';
import {
  selectDetectionsWithoutInterpolation,
  selectTDO,
} from '@worker/selectors';
import { ElementOf } from 'ts-essentials';
import { selectConfigEngines } from '@common/state/modules/engines/selectors';

// const VERSION = '1.1.0';
const VERSION = '1.1.1'; // updates to vendor: no longer has objectId, added subsegmentId - app commits prior to ~Oct 2024 will be unable to load this version because relied on loading objectId instead of dynamic generation
// const VERSION = '1.2.0'; // once we remove groupId from vendor as planned will update version again

/**
 * Takes data in the store and converts it to user-edited detection asset.
 * The main difference is preserving the IDs.
 */
export const detectionStoreV1Adapter = (): Adapter<
  WorkerStoreSlice,
  DetectionEngineOutputData
> => ({ isMatch, adapt });

// eslint-disable-next-line lodash/prefer-constant
const isMatch = (_: unknown): _ is WorkerStoreSlice => true;

const adapt: ReturnType<typeof detectionStoreV1Adapter>['adapt'] = (
  store: WorkerStoreSlice
) => {
  const detections = selectDetectionsWithoutInterpolation(store);

  const { detectionEngineCPU } = selectConfigEngines(store); // when saving use CPU (even if used GPU id)

  const { id: tdoId } = selectTDO(store) || { id: 'NA' };

  const s: ElementOf<DetectionEngineOutputData['series']>[] = [];
  let startMs = detections.length ? Infinity : 0;
  let stopMs = 0;

  for (const item of detections) {
    startMs = Math.min(startMs, item.startTimeMs);
    stopMs = Math.max(stopMs, item.stopTimeMs);
    s.push({
      // engineId: item.engineId, // don't need this
      startTimeMs: item.startTimeMs,
      stopTimeMs: item.stopTimeMs,
      object: {
        label: item.object.label,
        userLabel: item.userLabel,
        type: item.object.type,
        boundingPoly: item.object.boundingPoly,
        confidence: item.object.confidence,
      },
      vendor: {
        groupId: item.subsegmentId, // TODO remove this after next release - weird legacy naming convention when subsegments were called groups - keep for one more release so app can be rolled back if necessary
        ...(item.clusterId !== item.groupId && { clusterId: item.clusterId }), // save clusterId (when not same as groupId)
        subsegmentId: item.subsegmentId, // this is replacement for groupId - might just dynamically regenerate in the future instead
        // only bother saving isDeleted when true
        ...(item.object.isDeleted && { isDeleted: item.object.isDeleted }),
        // only save overlayObjectType if is an interpolation - otherwise it will default to a detection when reloaded
        ...(item.object.overlayObjectType === 'interpolation' && {
          overlayObjectType: item.object.overlayObjectType,
        }),
        shapeType: item.shapeType,
        redactionCode: item.redactionCode,
        redactionConfig: item.redactionConfig,
      },
    });
  }

  const series = sortBy(s, 'startTimeMs');
  const startOffsetMs = startMs;
  const stopOffsetMs = stopMs;

  return {
    tdoId: tdoId,
    assetId: store[namespace].detectionEngineResultsAssetId || '',
    engineId: detectionEngineCPU || 'NA',
    startOffsetMs,
    stopOffsetMs,
    // jsondata: {
    version: VERSION,
    generatedDateUTC: new Date().toISOString(),
    modifiedDateTime: Date.now(),
    sourceEngineId: detectionEngineCPU || 'NA',
    taskId: 'NA',
    series,
    // },
  };
};
